import { formatCurrency, formatDate, NgClass, NgStyle, NgTemplateOutlet } from "@angular/common";
import { Component, ElementRef, EventEmitter, HostListener, inject, Input, LOCALE_ID, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from "@angular/core";
import { ClassDirective, ShowHideDirective, StyleDirective } from "@ngbracket/ngx-layout/extended";
import { FlexDirective, LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { NoteAndAttachmentComponent } from "@tylertech/advanced-components-module";
import { DocumentService } from "@tylertech/attachments-module";
import { Command, CommandBarComponent, CommandComponent, IconCommand, LabelCommand } from "@tylertech/commands-module";
import { Chip, ChipCollectionComponent, DynamicChip } from "@tylertech/common-components-module";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import { isHttpResponseError, OmniBarService, RepositoryService, ThemeService, ToastService } from "@tylertech/core-module";
import { CardComponent } from "@tylertech/forge";
import { ForgeDividerModule, ForgeIconButtonModule, ForgeIconModule, ForgeTooltipModule } from "@tylertech/forge-angular";
import { IntentService } from "@tylertech/intents-module";
import { AugmentedModelObject, IMetadataLoadViewService, MetadataActionService, ObjectHelpers, RepositoryFormData, } from "@tylertech/metadata-module";
import { Utilities } from "@tylertech/shared-module";
import { DetailType, DocumentSearchCriteria, ExecutionResult, GetNoteObjectViewModel, IInfoCardAction, IInfoCardChip, IInfoCardData, IInfoCardDataElement, IInfoCardGroupList, IMetadataAction, InfoCardActionType, MetadataAction, } from "@tylertech/tyler-api-types";
import { Observable, Subject, Subscription } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { InfoCardCommandArgs, InfoCardTemplateContext } from "../../interfaces";
import { InfoCardSearchService, InfoCardService } from "../../services";
import { DetailTableComponent } from "../detailTable/detail.table.component";
import { CardAvatarComponent } from "./cardAvatar.component";
import { InfoCardDetailFieldComponent } from "./infoCardDetailField.component";

type DataWidth = Record<string, number>;

interface IExpandable
{
    isEmphasized?: boolean;
}

interface ISelectable
{
    selected?: boolean;
}

interface InfoCardMeta
{
    $$RepositoryFormData?: Record<string, RepositoryFormData>;
    $$LazyLoadComplete?: boolean;
}

type InfoCard = IInfoCardData & IExpandable & ISelectable & InfoCardMeta;

interface InfoCardActionMeta
{
    $Chip?: IInfoCardChip;
    $$RepositoryFormData?: RepositoryFormData;
}

type InfoCardAction = IInfoCardAction & InfoCardActionMeta;

interface InfoCardChipMeta
{
    $OriginalText?: string;
    Visible?: boolean;
}

type InfoCardChip = IInfoCardChip & InfoCardChipMeta;

function isInfoCardData(x: unknown): x is IInfoCardData
{
    return !!x
        && typeof x === "object"
        && "DataElements" in x;
}

@Component({
    selector: "info-card-view",
    templateUrl: "./infoCardView.tmpl.html",
    styleUrls: [
        "./infoCardView.component.scss"
    ],
    imports: [
        CardAvatarComponent,
        ChipCollectionComponent,
        ClassDirective,
        CommandBarComponent,
        CommandComponent,
        DetailTableComponent,
        FlexDirective,
        ForgeDividerModule,
        ForgeIconButtonModule,
        ForgeIconModule,
        ForgeTooltipModule,
        InfoCardDetailFieldComponent,
        LayoutAlignDirective,
        LayoutDirective,
        NgClass,
        NgStyle,
        NgTemplateOutlet,
        NoteAndAttachmentComponent,
        ShowHideDirective,
        StyleDirective,
    ]
})
export class InfoCardViewComponent implements OnDestroy, OnInit, OnChanges
{
    private readonly locale = inject(LOCALE_ID);
    private readonly $omniBarService = inject(OmniBarService);
    private readonly $themeService = inject(ThemeService);
    private readonly $commonDialogsService = inject(CommonDialogsService);
    private readonly $toastService = inject(ToastService);
    private readonly $documentApiService = inject(DocumentService);
    private readonly $intentService = inject(IntentService);
    private readonly $repositoryService = inject(RepositoryService);
    private readonly $metadataActionService = inject(MetadataActionService);
    private readonly $metadataLoadViewService = inject(IMetadataLoadViewService);
    private readonly $infoCardSearchService = inject(InfoCardSearchService);
    private readonly $infoCardService = inject(InfoCardService);

    @Input() readonly canDelete: boolean;

    /**
     * Only set to true for InfoCardCollections; which in turn is only used by:
     * \apps\vendormanager\src\components\vendorManager.tmpl.html
     * \library\incode9Shared\src\components\infoTableViewMetadata\infoTableViewMetadata.tmpl.html
     * @deprecated
     */
    // TODO - this should be pulled out... to places that rely on it should be changed...
    @Input("compact") readonly isCompact: boolean;

    /** Rendered below the card header when the provider sets `CardHeaderDetailType` to `Custom`. */
    @Input() readonly headerTemplate: TemplateRef<InfoCardTemplateContext>;
    /** Rendered in the expanded card body when the provider sets `CardDetailType` to `Custom`. */
    @Input() readonly detailTemplate: TemplateRef<InfoCardTemplateContext>;

    @Input() readonly editable: boolean;

    @Input() readonly expandable: boolean;
    @Input("expand") readonly isExpanded: boolean;

    @Input() readonly item: InfoCard;
    @Input() readonly hasClientActionDefined: boolean;
    @Output() private readonly itemChange = new EventEmitter<IInfoCardData>();

    @Input() readonly outlined: boolean;

    @Input()
    set refreshLayout(value: Observable<void>)
    {
        this.refreshLayoutSubscription?.unsubscribe();
        this.refreshLayoutSubscription = value.pipe(takeUntil(this.destructor$))
            .subscribe(() =>
            {
                this.setLayout();
            });
    }

    @Output() private readonly clientAction = new EventEmitter<InfoCardCommandArgs>();
    @Output() private readonly refreshCollection = new EventEmitter<void>();
    @Output() private readonly edit = new EventEmitter<void>();

    @ViewChild("card") private readonly cardRef?: ElementRef<CardComponent>;
    @ViewChild("compactDetail") private readonly compactDetail?: ElementRef<HTMLSpanElement>;

    private readonly destructor$ = new Subject<void>();
    readonly itemCommands: Array<Command> = [];
    /** Commands for the NotesAndAttachments component */
    readonly additionalCommands: Array<Command> = [];
    readonly iconCommands: Array<Command> = [];
    readonly chips: Array<Chip> = [];

    private refreshLayoutSubscription: Subscription;
    private _hasBarCommands: boolean;
    private _hasMenuCommands: boolean;
    private columnCount: number = 0;
    private maxWidthPrimary: Array<DataWidth> = [];
    private maxWidthSecondary: Array<DataWidth> = [];
    private _headerElements: Array<IInfoCardDataElement> = [];
    private _subHeaderElements: Array<IInfoCardDataElement> = [];
    private readonly _primaryElements: Record<string, Array<Array<IInfoCardDataElement>>> = {};
    private readonly _secondaryElements: Record<string, Array<Array<IInfoCardDataElement>>> = {};
    noteAttachmentEntity: GetNoteObjectViewModel;

    private get hasEmblemText(): boolean { return !!this.item.EmblemText; }
    private get hasPhoto(): boolean { return !!this.item.Photo?.Photo; }
    private get isSelected(): boolean { return this.item.selected; }

    get allowEdit(): boolean { return this.item?.AllowEdit || this.editable; }
    get allowDelete(): boolean { return this.item?.AllowDelete || this.canDelete; }
    get hasBarCommands(): boolean { return this._hasBarCommands; }
    get hasHeader(): boolean { return this.hasHeaderElements || !!this.item.EmblemText; }
    get hasHeaderElements(): boolean { return this.item.HeaderElements?.length > 0; }
    get hasSubHeader(): boolean { return this.subHeaderElements?.length > 0; }
    get hasVertCommands(): boolean { return this._hasMenuCommands; }
    get headerElements(): ReadonlyArray<IInfoCardDataElement> { return this._headerElements; }
    get isEmphasized(): boolean { return this.item.isEmphasized; }
    get isHeaderCustom(): boolean { return this.item.CardHeaderDetailType === DetailType.Custom; }
    get isDetailCustom(): boolean { return this.item.CardDetailType === DetailType.Custom; }
    get isDetailTable(): boolean { return this.item.CardDetailType === DetailType.Table; }
    get subHeaderElements(): ReadonlyArray<IInfoCardDataElement> { return this._subHeaderElements; }

    get cardClasses(): Record<string, boolean>
    {
        /* eslint-disable @typescript-eslint/naming-convention */
        return {
            "info-card-no-expnd": !this.expandable,
            "card-Selected": this.isSelected,
            "emph": this.isEmphasized,
        };
        /* eslint-enable @typescript-eslint/naming-convention */
    }

    get cardHeaderClasses(): Record<string, boolean>
    {
        /* eslint-disable @typescript-eslint/naming-convention */
        return {
            "header-expanded": this.isEmphasized,
            "info-card-header": this.shouldHaveNoBottomMargin()
        };
        /* eslint-enable @typescript-eslint/naming-convention */
    }

    get headerGroupClasses(): Record<string, boolean>
    {
        /* eslint-disable @typescript-eslint/naming-convention */
        return {
            "info-header-group": !this.isCompact
        };
        /* eslint-enable @typescript-eslint/naming-convention */
    }

    get hasAggregates(): boolean { return this.item.AggregateElements?.length > 0; }

    get groupDataFieldClasses(): Record<string, boolean>
    {
        /* eslint-disable @typescript-eslint/naming-convention */
        return {
            'primary-expanded': this.isEmphasized
        };
        /* eslint-enable @typescript-eslint/naming-convention */
    }

    get chevronIcon(): "expand_more" | "expand_less"
    {
        return this.isEmphasized
            ? "expand_less"
            : "expand_more";
    }

    get showVertMenu(): boolean
    {
        return this.item.AllowAttachments
            || this.item.AllowNotes
            || this.hasVertCommands
            || this.item.Chips?.length > 0;
    }

    get showAvatar(): boolean { return this.hasPhoto || this.hasEmblemText; }

    @HostListener("window:resize")
    onResize(): void
    {
        this.setLayout();
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        this.loadCardView();
    }

    ngOnChanges(_changes: SimpleChanges): void
    {
        // TODO - Any change what so ever should trigger loadCardView?
        this.loadCardView();
    }

    private loadCardView(): void
    {
        if (this.isExpanded)
        {
            this.toggleEmphasis(this.item);
        }

        this.setHeaderElements();
        this.setupCommands();
        this.setupChips();

        this.noteAttachmentEntity = {
            Id: this.item.Id,
            Type: this.item.EntityName,
        };

        // TODO - why timeout?
        setTimeout(
            () =>
            {
                if (this.item.GroupDataElements)
                {
                    this.setLayout();
                }
            },
            50);
    }

    private setHeaderElements(): void
    {
        this._headerElements = [];
        this._subHeaderElements = [];
        this.item.HeaderElements?.forEach(x =>
        {
            if (!x.Visible)
            {
                return;
            }

            if (x.Type === "Header")
            {
                this._headerElements.push(x);
            }

            if (x.Type === "Sub")
            {
                this._subHeaderElements.push(x);
            }
        });
    }

    private setupCommands(): void
    {
        this.itemCommands.length = 0;
        this.additionalCommands.length = 0;
        this.iconCommands.length = 0;

        this.filteredCommands()?.forEach(
            (infoCardAction: InfoCardAction) =>
            {
                if (infoCardAction.ActionType === InfoCardActionType.Icon)
                {
                    this.iconCommands.push(
                        new IconCommand({
                            icon: infoCardAction.Icon,
                            color: () => this.getIconCommandColor(infoCardAction),
                            getEnabled: () => infoCardAction.Enabled,
                            tooltip: infoCardAction.Label,
                            action: () => this.executeCommand(
                                {
                                    command: infoCardAction,
                                    item: this.item,
                                }),

                        }));
                }

                if (infoCardAction.Action === "Metadata")
                {
                    infoCardAction.$$RepositoryFormData = this.getMetadataFormData(infoCardAction);
                }

                if (infoCardAction.ActionType === InfoCardActionType.Command)
                {
                    this._hasBarCommands = true;
                    this.itemCommands.push(
                        new LabelCommand({
                            label: infoCardAction.Label,
                            tooltip: infoCardAction.Label,
                            getEnabled: () => infoCardAction.Enabled,
                            getVisible: () => infoCardAction.Visible,
                            action: () => this.executeCommand(
                                {
                                    command: infoCardAction,
                                    item: this.item
                                })
                        }));
                }

                if (infoCardAction.ActionType === InfoCardActionType.Menu)
                {
                    this._hasMenuCommands = true;
                    this.additionalCommands.push(
                        new LabelCommand({
                            label: infoCardAction.Label,
                            action: () => this.executeCommand(
                                {
                                    command: infoCardAction,
                                    item: this.item
                                }),
                            getEnabled: () => infoCardAction.Enabled,
                            getVisible: () => infoCardAction.Visible
                        }));
                }
            });

        // AddChipCommands
        this.item.Chips?.reduce(
            (l: Array<Command>, chip: InfoCardChip) =>
            {
                if (chip.Text.indexOf(" $count$") === 0 && chip.Action.Action !== "Metadata")
                {
                    return l;
                }

                (chip.Action as InfoCardAction).$Chip = chip;
                chip.$OriginalText = chip.Text;
                chip.Visible = false;
                this.loadCountChip(chip);

                const text = chip.Text.replace(" $count$", "");
                const label = `Add ${text}`;
                const cmd = new LabelCommand({
                    label,
                    tooltip: label,
                    action: () => this.executeCommand(
                        {
                            command: chip.Action,
                            item: this.item
                        }),
                });

                l.push(cmd);

                return l;
            },
            this.additionalCommands);
    }

    filteredCommands(): IInfoCardAction[]
    {
        if (!this.hasClientActionDefined && !Utilities.IsNullOrUndefinedOrEmpty(this.item.Commands))
        {
            return this.item.Commands.filter(x => x.Action !== "Client");
        }
        return this.item.Commands;
    }

    private setupChips(): void
    {
        this.chips.length = 0;
        this.item.Chips?.reduce(
            (list: Array<Chip>, chip: InfoCardChip) =>
            {
                list.push(
                    new DynamicChip(
                        () => chip.Text,
                        () =>
                        {
                            this.executeCommand({
                                command: chip.Action,
                                item: this.item
                            });
                        },
                        () =>
                        {
                            return chip.Visible !== undefined
                                ? chip.Visible
                                : true;
                        }));
                return list;
            },
            this.chips);
    }

    private getMetadataFormData(cmd: IInfoCardAction): RepositoryFormData
    {
        const metadataAction: IMetadataAction = JSON.parse(cmd.Data[0]);
        return this.$metadataActionService.GetNewRepositoryFormData(metadataAction);
    }

    onCardClick(): void
    {
        if (!this.expandable)
        {
            return;
        }

        this.toggleEmphasis(this.item);

    }

    toggleEditMode(): void
    {
        this.edit.emit();
    }

    onEdit($event?: MouseEvent): void
    {
        $event?.stopPropagation();

        this.executeClientAction("edit");
    }

    onDelete($event: MouseEvent): void
    {
        $event?.stopPropagation();

        this.executeClientAction("delete");
    }

    private executeClientAction(action: "edit" | "delete"): void
    {
        // ?REVIEW - Why is clientAction using a Argument type with so much more optional info that we are ignoring?
        this.clientAction.emit({
            command: {
                Action: action,
                ActionType: undefined,
                ConfirmAction: undefined,
                Data: undefined,
                Enabled: undefined,
                Icon: undefined,
                IconColor: undefined,
                Label: undefined,
                MimeType: undefined,
                ValidationMsg: undefined,
                Visible: undefined,
            },
            item: this.item,
        });
    }

    getAggregateElementColor(element: IInfoCardDataElement): Record<string, string>
    {
        return null;

        // return {
        //     "color": element.Color || this.$themeService.currentTheme.aggregateElementColor
        // };
    }

    getClass(field: IInfoCardDataElement): string
    {
        const className = field.Name.replace(/ /g, "-");
        return className;
    }

    private getIconCommandColor(command: IInfoCardAction): string
    {
        return null;
        // return command.Enabled
        //     ? command.IconColor
        //     : this.$themeService.currentTheme.iconCommandColor;
    }

    getValue(field: IInfoCardDataElement): string
    {
        if (!field.Value)
        {
            return "";
        }

        if (typeof field.Value === "object")
        {
            return field.Value.Value;
        }

        if (this.item.Metadata)
        {
            const prop = this.item.Metadata.Properties.find(p => p.Name === field.Name);
            if (prop)
            {
                switch (prop.InputType)
                {
                    case "Date":
                        return formatDate(field.Value, "M/d/yyyy", this.locale);
                    case "Currency":
                        return formatCurrency(field.Value, this.locale, "1.2");
                }
            }
        }
        return field.Value.toString();
    }

    hasItems(group: string): boolean
    {
        const groupElements = this._secondaryElements[group];
        return !!groupElements && groupElements.length > 0;
    }

    // TODO - flip boolean to showPrimaryDetail
    hidePrimaryDetail(groupName: string): boolean
    {
        return !this.item.DataElements
            || this.item.DataElements.filter(x => x.Type === "Primary").length === 0
            || !this._primaryElements[groupName]
            || this._primaryElements[groupName].length === 0
            || this.isCompact;
    }

    // TODO - flip boolean to showTitle
    hideTitle(groupName: string): boolean
    {
        if (!this._primaryElements || !this._primaryElements[groupName])
        {
            return true;
        }

        if (this.expandable && this.isEmphasized)
        {
            return groupName === ""
                || (this._primaryElements[groupName].length === 0
                    && this._secondaryElements[groupName].length === 0);
        }
        return groupName === ""
            || this._primaryElements[groupName].length === 0;
    }

    isAction(item: IInfoCardDataElement): boolean
    {
        return !!item.ActionType || item.InfoCardAction?.ActionType === InfoCardActionType.LinkLabel;
    }

    private executeCommand($event: InfoCardCommandArgs): void
    {
        if ($event.command.Action === "Client")
        {
            this.executeClientCommand($event);
        }
        else if ($event.command.Action === "Server")
        {
            this.executeServerCommand($event);
        }
        else if ($event.command.Action === "Metadata")
        {
            this.executeMetadataCommand($event);
        }
        else
        {
            let parms: Record<string, unknown> | Array<string> = {};
            $event.command.Data.forEach((value, index) =>
            {
                if (index % 2 === 0)
                {
                    parms[value] = $event.command.Data[index + 1];
                }
            });

            if ($event.command.Action === "MetadataForm")
            {
                this.$infoCardSearchService.fireSearchActionListeners(parms);
            }
            else
            {
                if (Object.keys(parms).length === 0 && $event.command.Data)
                {
                    parms = $event.command.Data;
                }
                this.$intentService.resolve($event.command.MimeType, $event.command.Action, parms);
            }
        }
    }

    private executeMetadataCommand($event: InfoCardCommandArgs): void
    {
        if (!$event.item.Entity && !!$event.item.Metadata)
        {
            this.$repositoryService.Get(
                {
                    Item1: $event.item.Metadata.TypeName,
                    Item2: $event.item.Id,
                })
                .then(entity =>
                {
                    $event.item.Entity = entity;
                    this.performMetadataAction($event.command, $event.item);
                });
        }

        else
        {
            this.performMetadataAction($event.command, $event.item);
        }
    }

    private executeServerCommand($event: InfoCardCommandArgs): void
    {
        if ($event.command.ConfirmAction)
        {
            this.$commonDialogsService.showConfirmationMessage(
                `Are you sure you want to ${$event.command.Label}?`,
                "Confirm Action",
                "Confirm Action")
                .then(
                    () =>
                    {
                        this.performServerCommand($event.command);
                    },
                    () =>
                    {
                        /* NO_OP */
                    }
                );
        }

        else
        {
            this.performServerCommand($event.command);
        }
    }

    private executeClientCommand($event: InfoCardCommandArgs): void
    {
        this.clientAction.emit({
            command: $event.command,
            item: $event.item,
        });
    }

    performFieldCommand(field: IInfoCardDataElement, $event?: MouseEvent): void
    {
        $event?.stopPropagation();

        if (field.InfoCardAction?.ActionType !== InfoCardActionType.LinkLabel)
        {
            return;
        }

        const data: Record<string, unknown> = {};
        if (!field.InfoCardAction.Data || field.InfoCardAction.Data.length < 2)
        {
            data.id = this.item.Id;
        }
        else
        {
            for (let i = 1; i < field.InfoCardAction.Data.length; i = i + 2)
            {
                data[field.InfoCardAction.Data[i - 1]] = field.InfoCardAction.Data[i];
            }
        }

        this.$intentService.resolve(field.InfoCardAction.MimeType, field.InfoCardAction.Action, data);
    }

    private performMetadataAction(command: InfoCardAction, item: InfoCard): void
    {
        const action: MetadataAction = JSON.parse(command.Data[0]);
        let repositoryFormData: RepositoryFormData = null;
        if (item.$$RepositoryFormData)
            repositoryFormData = item.$$RepositoryFormData[command.Label];
        this.$metadataActionService.ExecuteAction(item.Entity, action, repositoryFormData)
            .then(
                (result: unknown) =>
                {
                    const chip = command.$Chip;
                    if (chip)
                    {
                        this.loadCountChip(chip, true);
                        return;
                    }

                    else if (command.ValidationMsg)
                    {
                        this.$toastService.showToast(command.ValidationMsg);
                    }

                    if (action.Context && !action.ReturnPath && !action.ServicePath)
                    {
                        action.Context = result;
                        command.Data[0] = ObjectHelpers.stringifyCircular(action);
                    }

                    this.refresh();

                    if (isInfoCardData(result))
                    {
                        this.itemChange.emit(result);
                        this.setHeaderElements();
                        this.setLayout();
                    }
                },
                (_error: unknown) =>
                {
                    const chip = command.$Chip;
                    if (chip)
                    {
                        this.loadCountChip(chip, true);
                        return;
                    }
                }
            );
    }

    private loadCountChip(chip: InfoCardChip, forceReload?: boolean): void
    {
        const entity: AugmentedModelObject = this.item.Entity || createNewEntity(this.item);
        const metadataAction: IMetadataAction = JSON.parse(chip.Action.Data[0]);

        if (metadataAction.ViewToShow?.IsTCMAttachmentView)
        {
            this.loadDocumentChipCount(entity, metadataAction, chip);
            return;
        }

        if (!this.item.$$RepositoryFormData) this.item.$$RepositoryFormData = {};

        const repositoryFormData = (!forceReload && this.item.$$RepositoryFormData[chip.Action.Label])
            ? this.item.$$RepositoryFormData[chip.Action.Label]
            : new RepositoryFormData(null, null);
        if (!Utilities.IsNullOrUndefined(metadataAction.ViewToShow))
        {
            this.$metadataLoadViewService.LoadView(entity, metadataAction.ViewToShow, repositoryFormData)
                .then(
                    (x: Array<AugmentedModelObject>) =>
                    {
                        this.item.$$RepositoryFormData[chip.Action.Label] = repositoryFormData;
                        const count = x ? x.length : 0;
                        chip.Text = chip.$OriginalText.replace(" $count$", "(" + count + ")");
                        chip.Visible = count > 0 && !x[0].HideChip;
                    },
                    _err =>
                    {
                        chip.Text = chip.Text.replace("$count$", "0");
                        chip.Visible = false;
                    }
                );
        }

        function createNewEntity(cardData: IInfoCardData): Record<string, unknown>
        {
            return cardData.DataElements
                .reduce((p, x) =>
                {
                    p[x.Name] = x.Value;
                    return p;
                }, {});
        }
    }

    private loadDocumentChipCount(entity: AugmentedModelObject, metadataAction: IMetadataAction, chip: InfoCardChip): void
    {
        const criteria: DocumentSearchCriteria = {
            EntityId: entity[metadataAction.ViewToShow.ParentColumnName],
            EntityType: metadataAction.ViewToShow.ViewMetaData.TypeName,
            DocType: undefined,
            Entity: undefined,
            Mode: 0,
            OptionalCollection: undefined,
            ReturnMetadataOnly: undefined,
            KeysOnly: undefined
        };
        this.$documentApiService.GetDocumentCount(criteria)
            .then(
                (result: ExecutionResult<number>) =>
                {
                    const count = result.Result;
                    chip.Text = chip.$OriginalText?.replace(" $count$", "(" + count + ")");
                    chip.Visible = count > 0;
                },
                _err =>
                {
                    chip.Text = chip.Text.replace("$count$", "0");
                    chip.Visible = false;
                }
            );
    }

    private performServerCommand(command: IInfoCardAction): void
    {
        this.$infoCardService.PerformAction(
            {
                Id: this.item.Id,
                ProviderId: this.item.CardProviderId,
                Action: command,
            })
            .then(
                result =>
                {
                    if (!result)
                    {
                        return;
                    }

                    this.$commonDialogsService.showAlertHtmlMessage(
                        result, "Success");
                },
                (error: unknown) =>
                {
                    if (isHttpResponseError(error))
                    {
                        this.$commonDialogsService.showAlertHtmlMessage(error.error.Message, "Failed");
                        return;
                    }
                    console.error(error);
                }
            );
    }

    private refresh(): void
    {
        if (this.item.ReloadAfterAction)
        {
            this.refreshCollection.emit();
        }
    }

    private setLayout(): void
    {
        this.item.GroupDataElements.forEach(element =>
        {
            this.setLayoutByGroup(element);
        });
    }

    private setLayoutByGroup(group: IInfoCardGroupList): void
    {
        this.columnCount = 0;
        const compactDetail = this.compactDetail?.nativeElement;
        const width = this.isCompact && compactDetail
            ? compactDetail.offsetWidth
            : this.cardRef.nativeElement.offsetWidth;

        // TODO - what are these magic numbers?
        const columnWidth = this.isCompact ? 100 : 150;
        const columnOffset = this.isCompact ? 100 : 300;

        if (!width || width <= 0 || !this.item.DataElements)
        {
            return;
        }

        const [primary, secondary] = this.item.DataElements.reduce(
            ([primary, secondary]: [Array<IInfoCardDataElement>, Array<IInfoCardDataElement>], element: IInfoCardDataElement) =>
            {
                const set = element.Type === "Primary"
                    ? primary : element.Type === "Secondary"
                        ? secondary
                        : undefined;

                if (set
                    && element.Visible
                    && element.Group === group.GroupName
                    && element.Label !== group.GroupName)
                {
                    set.push(element);
                }

                return [primary, secondary];
            },
            [[], []]);

        const columnCount = Math.max(1, Math.round((width - columnOffset) / columnWidth));
        if (columnCount <= primary.length)
        {
            this.maxWidthPrimary[group.GroupName] = Math.round(100 / columnCount);
            console.debug(
                `Setting primary column width`,
                {
                    groupName: group?.GroupName || "",
                    columnCount,
                    maxWidth: this.maxWidthPrimary[group.GroupName]
                });
        }

        if (columnCount <= secondary.length)
        {
            this.maxWidthSecondary[group.GroupName] = Math.round(100 / columnCount);
            console.debug(
                `Setting secondary column width`,
                {
                    groupName: group.GroupName || "",
                    columnCount,
                    maxWidth: this.maxWidthSecondary[group.GroupName]
                });
        }

        if (this.columnCount === columnCount)
        {
            return;
        }

        this.columnCount = columnCount;

        this._primaryElements[group.GroupName] = this.chunk(primary, columnCount);
        this._secondaryElements[group.GroupName] = this.chunk(secondary, columnCount);
    }

    // TODO - should this be in some array utilities class somewhere?
    private chunk<T = unknown>(originalArray: Array<T>, size: number): Array<Array<T>>
    {
        if (!originalArray)
        {
            return [];
        }

        const newArr: Array<Array<T>> = [];
        for (let i = 0; i < originalArray.length; i += size)
        {
            newArr.push(originalArray.slice(i, i + size));
        }
        return newArr;
    }

    private shouldHaveNoBottomMargin(): boolean
    {
        return this.item.DataElements.filter(d => d.Type === "Primary" && d.Visible).length > 0 && this.hasEmblemText;
    }

    private toggleEmphasis(item: IExpandable, $event?: MouseEvent): void
    {
        $event?.stopPropagation();
        item.isEmphasized = !item.isEmphasized;
    }

    getHeaderElementClasses(isLast: boolean): Record<string, boolean>
    {
        /* eslint-disable @typescript-eslint/naming-convention */
        return {
            "content-display": isLast
        };
        /* eslint-enable @typescript-eslint/naming-convention */
    }

    getHeaderActionAriaLabel(field: IInfoCardDataElement): string
    {
        return `Submission-${field.Label}`;
    }

    getPrimaryFieldsForGroup(group?: IInfoCardGroupList): ReadonlyArray<ReadonlyArray<IInfoCardDataElement>>
    {
        return this._primaryElements[group?.GroupName || ""];
    }

    getPrimaryFieldWidth(group?: IInfoCardGroupList): number
    {
        return this.maxWidthPrimary[group?.GroupName || ""];
    }

    getSecondaryFieldsForGroup(group: IInfoCardGroupList): ReadonlyArray<ReadonlyArray<IInfoCardDataElement>>
    {
        return this._secondaryElements[group.GroupName];
    }

    getSecondaryFieldWidth(group: IInfoCardGroupList): number
    {
        return this.maxWidthSecondary[group.GroupName];
    }
}
